import { useEffect, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Clock, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [now, setNow] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/attendance/today").then((r) => setToday(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);

  const clockIn = async () => {
    setLoading(true);
    try { await api.post("/attendance/clock-in"); toast.success("Clocked in"); load(); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  const clockOut = async () => {
    setLoading(true);
    try { await api.post("/attendance/clock-out"); toast.success("Clocked out"); load(); }
    catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };

  const loggedIn = today?.login_time && !today?.logout_time;
  const done = today?.login_time && today?.logout_time;

  return (
    <div className="space-y-8" data-testid="employee-dashboard">
      <div>
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">00 / Today</div>
        <h1 className="rule-text text-3xl sm:text-4xl text-zinc-900 dark:text-zinc-100">Hello, {user?.name?.split(" ")[0] || "Employee"}.</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 bg-zinc-950 text-zinc-50 p-10 flex flex-col justify-between min-h-[260px]">
          <div className="flex items-center gap-3">
            <Clock size={16} /><span className="eyebrow text-zinc-400 dark:text-zinc-500">Live Clock · UTC{-new Date().getTimezoneOffset()/60 >= 0 ? '+' : ''}{-new Date().getTimezoneOffset()/60}</span>
          </div>
          <div>
            <div className="rule-text text-7xl tabular-nums">{now.toLocaleTimeString("en-GB")}</div>
            <div className="text-sm text-zinc-400 mt-2">{now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
          <div className="flex gap-2">
            {!today?.login_time && (
              <Button onClick={clockIn} disabled={loading} className="klein-btn rounded-xl uppercase tracking-[0.2em] text-xs font-bold h-12 px-8" data-testid="clock-in-btn">
                <LogIn size={14} className="mr-2" /> Mark Attendance · Clock In
              </Button>
            )}
            {loggedIn && (
              <Button onClick={clockOut} disabled={loading} className="rounded-xl bg-white text-zinc-950 hover:bg-zinc-100 dark:bg-zinc-800 uppercase tracking-[0.2em] text-xs font-bold h-12 px-8" data-testid="clock-out-btn">
                <LogOut size={14} className="mr-2" /> Clock Out
              </Button>
            )}
            {done && <div className="eyebrow text-emerald-400">Attendance marked · {today.working_hours} hrs</div>}
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6 space-y-4">
          <div className="eyebrow">Today Status</div>
          <div className="text-sm space-y-3">
            <div className="flex justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock-In</span><span className="font-mono">{today?.login_time ? new Date(today.login_time).toLocaleTimeString("en-GB") : "—"}</span></div>
            <div className="flex justify-between border-b border-zinc-100 dark:border-zinc-800 pb-2"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Clock-Out</span><span className="font-mono">{today?.logout_time ? new Date(today.logout_time).toLocaleTimeString("en-GB") : "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500 dark:text-zinc-400 dark:text-zinc-500">Hours</span><span className="font-mono font-bold">{today?.working_hours || 0}</span></div>
          </div>
          <div className="pt-4 border-t border-zinc-200 dark:border-zinc-800">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-indigo-600 mb-2">Rules</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
              ≥ 4 hrs = full day (₹166.67)<br/>
              &lt; 3 hrs = hourly (₹41.67/hr)
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <InfoCard label="Employee ID" value={user?.employee_id} />
        <InfoCard label="Username" value={user?.username} />
        <InfoCard label="Role" value={user?.role} />
      </div>
    </div>
  );
}

const InfoCard = ({ label, value }) => (
  <div className="bg-white dark:bg-zinc-900 border border-zinc-100 dark:border-zinc-800 rounded-xl shadow-sm p-6">
    <div className="eyebrow">{label}</div>
    <div className="rule-text text-2xl mt-2 break-all">{value || "—"}</div>
  </div>
);
